import { type TFunction } from '@cms/i18n/shared';
import { logger } from '@common/logger';

export const IMAGE_MIME_STRINGS = ['image/png', 'image/jpeg'];
export const VIDEO_MIME_STRINGS = ['video/mp4', 'audio/mpeg'];

export const MB = 1000000;
export const MAX_FILESIZE = 1000 * MB;
export const MAX_FILESIZE_PER_MINUTE = 10 * MB;
export const MAX_FILESIZE_PER_MINUTE_LONG = 25 * MB;
export const LONG_VIDEO_THRESHOLD = 60 * 5; // 5 min
export const DEFAULT_TIMEOUT = 5 * 60 * 1000; // 5 sec

export const checkMimeType = (
    event: any,
    props: any,
    setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>,
    __components: TFunction,
) => {
    //getting file object
    const files = event.target.files;

    // list allow mime types
    let types: string[] = [];
    if (props.acceptedMimeTypes) {
        types = props.acceptedMimeTypes;
    } else if (props.fileType === 'video') {
        types = VIDEO_MIME_STRINGS; //other extensions can be added here
    } else if (props.fileType === 'image') {
        types = IMAGE_MIME_STRINGS; //other extensions can be added here
    }

    // compare file type find if doesn't matach
    if (types.every((type) => files[0].type !== type)) {
        setErrorMessage(__components('FileUpload.typeError', { type: files[0].type }));
        return false;
    }

    return true;
};

export function formatFilename(filename: string, maxLength = 50): string {
    return filename.length <= maxLength ? filename : filename.slice(0, 60) + '....';
}

export const checkMultipleUploads = (
    event: any,
    setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>,
    __components: TFunction,
) => {
    if (event.target.files.length > 1) {
        setErrorMessage(__components('FileUpload.multiUploadError'));
        return false;
    } else {
        return true;
    }
};

export const checkFileSize = (
    event: any,
    setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>,
    __components: TFunction,
) => {
    const files = event.target.files;
    if (files[0].size > MAX_FILESIZE) {
        setErrorMessage(__components('FileUpload.sizeError'));
        return false;
    }

    return true;
};

export const fileSelectHandler = async (
    event: React.ChangeEvent<HTMLInputElement>,
    props: any,
    setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>,
    setSelectedVideoDuration: React.Dispatch<React.SetStateAction<number | undefined>>,
    setSelectedFile: React.Dispatch<React.SetStateAction<File | undefined>>,
    setSelectedFileName: React.Dispatch<React.SetStateAction<string | undefined>>,
    setLoaded: React.Dispatch<React.SetStateAction<number>>,
    field: any,
    __components: TFunction,
) => {
    const file = event.target.files?.[0];
    setErrorMessage(undefined);
    setSelectedVideoDuration(undefined);

    //validating the type, size amd number of uploaded videos
    if (
        !checkMimeType(event, props, setErrorMessage, __components) ||
        !checkFileSize(event, setErrorMessage, __components) ||
        !checkMultipleUploads(event, setErrorMessage, __components) ||
        !file
    ) {
        field.onChange(null);
    } else {
        setSelectedFile(file);
        const fileData = await props.endpoint(file, setLoaded).catch(() => {
            setErrorMessage(__components('FileUpload.uploadError'));
        });

        if (fileData?.filename) {
            setSelectedFileName(fileData.filename);
            if (props.fileType === 'video') {
                // create the video element but don't add it to the page
                const vid = document.createElement('video');
                // create url to use as the src of the video
                const fileURL = URL.createObjectURL(file);
                vid.src = fileURL;
                vid.ondurationchange = function () {
                    logger.debug('Video duration: ', Math.ceil(vid.duration));
                    setSelectedVideoDuration(Math.ceil(vid.duration));
                    vid.remove();
                };
            }

            field.onChange(fileData);
        }
    }
};

export const getMaxFileSizePerMinute = (selectedVideoDuration: number) => {
    return selectedVideoDuration && selectedVideoDuration > LONG_VIDEO_THRESHOLD
        ? MAX_FILESIZE_PER_MINUTE_LONG
        : MAX_FILESIZE_PER_MINUTE;
};
